/**
 * A whole sheet → its pages' display lists: pages expanded per floor, field
 * values resolved, per-page placeholders (page number, floor, scale) built,
 * every page laid out. Viewport content comes from the caller (plan vectors,
 * raster renders), keyed by page instance and viewport.
 */

import {
  expandPages,
  placeholderValues,
  resolveFieldValues,
  resolvePageViewports,
  type GradientDefinition,
  type GradientType,
  type ImageSource,
  type PageInstance,
  type PlaceholderSources,
  type PrintFloorInfo,
  type PrintTemplateSpec,
  type ResolvedViewport,
  type SheetDoc,
} from 'shared-types';
import type { DisplayItem, DisplayPage } from './displayList';
import type { FontResolver } from './fonts';
import { layoutPage, type ImageResource, type ViewportContent } from './layoutPage';

export interface DocumentLayoutInput {
  spec: PrintTemplateSpec;
  sheet: SheetDoc;
  floors: readonly PrintFloorInfo[];
  /** Project / organisation / sheet / user values; page, floor, field and scale values are added per page. */
  sources: Omit<PlaceholderSources, 'page' | 'floor' | 'fields' | 'viewportScales' | 'revision'>;
  date: Date;
  missing: 'empty' | 'marker';
  fonts: FontResolver;
  image: (source: ImageSource) => ImageResource | null;
  /** Content of one viewport on one page instance; null = nothing (yet). */
  viewportContent: (instance: PageInstance, viewport: ResolvedViewport) => DisplayItem[] | null;
  gradient?: (name: GradientType) => GradientDefinition | null;
}

export interface LaidOutPage {
  instance: PageInstance;
  viewports: ResolvedViewport[];
  page: DisplayPage;
}

/** The floor `{floor.*}` names: the per-floor instance's, else the floor the page's first viewport shows. */
function pageFloor(instance: PageInstance, viewports: readonly ResolvedViewport[], floors: readonly PrintFloorInfo[]): { name: string; elevation: number } | null {
  const floor = instance.floor ?? floors.find((f) => f.id === viewports[0]?.settings.floorId) ?? null;
  return floor ? { name: floor.name, elevation: floor.elevation } : null;
}

export function layoutDocument(input: DocumentLayoutInput): LaidOutPage[] {
  const { spec, sheet, date, missing } = input;
  const instances = expandPages(spec, sheet, input.floors);
  const base = placeholderValues(input.sources);
  const fields = resolveFieldValues(spec.fields, sheet.fieldValues, base, { date, missing });
  const latestRevision = sheet.revisions.length > 0 ? sheet.revisions[sheet.revisions.length - 1].rev : undefined;
  return instances.map((instance) => {
    const viewports = resolvePageViewports(sheet, instance);
    const values = placeholderValues({
      ...input.sources,
      sheet: input.sources.sheet ? { ...input.sources.sheet, rev: input.sources.sheet.rev ?? latestRevision } : undefined,
      page: { number: instance.number, count: instances.length },
      floor: pageFloor(instance, viewports, input.floors),
      fields,
      viewportScales: viewports.map((v) => ({ elementId: v.element.id, scaleDen: v.settings.scaleDen })),
    });
    const contents = new Map<string, ViewportContent>(viewports.map((v) => [v.element.id, { settings: v.settings, items: input.viewportContent(instance, v) }]));
    const page = layoutPage({ spec, page: instance.page, values, revisions: sheet.revisions, date, missing, fonts: input.fonts, image: input.image, viewports: contents, units: input.sources.units, legendLabels: sheet.legendLabels, gradient: input.gradient });
    return { instance, viewports, page };
  });
}
